import { splitEntity } from "shelving";
import { Icon, MediaImage, MenuButton } from "shared/react";
import { type ProfileData, getProfilePath } from "shared/schema";
import { useUsernameItem } from "../util/data.js";
import { useItem } from "../util/db.js";
import { useRewardStyle } from "../util/reward.js";
import { ProfileMenu } from "./ProfileMenu.js";
import { ProfileStats } from "./ProfileStats.js";

export function RawProfileCard({
	uid,
	profile,
	children = uid ? <MenuButton menu={<ProfileMenu uid={uid} />} mods="button-quiet pin-top pin-right" /> : null,
}: {
	uid?: string | undefined;
	profile: ProfileData;
	children?: React.ReactNode;
}): React.ReactElement {
	const { name, username, background, photo } = profile;
	const path = uid ? getProfilePath(profile) : undefined;
	return (
		<article className="card max-width-narrow pad-none targeted" style={useRewardStyle(background)}>
			<div className="flex-row">
				<div className="photo size-5x">{photo ? <MediaImage path={photo} /> : <Icon icon="profile" />}</div>
				<div className="pad-xsmall text-left">
					<h3 className="space-none">
						<a href={path} className="target">
							{name ? (
								<>
									{name} <small>{`@${username}`}</small>
								</>
							) : (
								`@${username}`
							)}
						</a>
					</h3>
					<p className="space-xsmall flex-row flex-left gap-xsmall size-small">
						<ProfileStats {...profile} />
					</p>
				</div>
			</div>
			{children}
		</article>
	);
}

export function ProfileCard({ uid, children }: { uid: string; children?: React.ReactNode }): React.ReactElement {
	const profile = useItem("profile", uid).data;
	return <RawProfileCard uid={uid} profile={profile} children={children} />;
}

export function UsernameProfileCard({ username }: { username: string }): React.ReactElement | null {
	const store = useUsernameItem(username);
	if (store.exists) {
		const [type, id] = splitEntity(store.data.creator);
		if (type === "profile") return <ProfileCard uid={id} />;
	}
	return null;
}
